
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { GitBranch, User, Users } from 'lucide-react';
import { useReferralPlan } from '@/hooks/useReferralPlan';
import { isAlternateDomain } from '@/utils/domainUtils';

const ReferralOpportunityHero: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);


  // Scroll to the calculator section when "See Our Residual Income Calculator" is clicked
  const scrollToResidualIncomeCalculator = () => {
    const residualIncomeCalculatorSection = document.getElementById('residual-income-calculator');
    if (residualIncomeCalculatorSection) {
      residualIncomeCalculatorSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plan = useReferralPlan();
  const maxLvl = String(plan.maxLevels);
  const r = (s: string) => s.replace(/\{maxLevels\}/g, maxLvl);
  const tiers = plan.tiers.map(t => ({ level: t.level, commission: t.commission, delay: 0 }));
  const isAlt = isAlternateDomain();

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gray-900 overflow-hidden">
        {/* Background image — adjust opacity (0 to 1) and blend mode to control visibility */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/background_images/referral_program_background.jpg')",
            opacity: 0.2,           // ← tweak this (0–1) to control image visibility
            mixBlendMode: 'normal', // ← try: 'normal', 'overlay', 'soft-light', 'luminosity', 'screen'
          }}
        />
        {/* Gradient overlays for depth and edge fading */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-transparent to-gray-900" style={{ opacity: 0.7 }} />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-transparent to-gray-900" style={{ opacity: 0.5 }} />
        {/* Ambient glow accents */}
        <div className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full bg-purple-500/10 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-indigo-500/10 blur-3xl"></div>

        <div className="container mx-auto px-4 mt-20 relative z-10">
          <div className="text-center">
            {/* Logo */}
            <div className="mb-8">
              <img
                src="/logo_1_inverted_no_background.png"
                alt="The Club Logo"
                className="h-20 w-auto mx-auto"
              />
            </div>

            {/* Referral Section */}
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll">
                    {t('referral.header')}
                  </h2>

                  <p className="text-3xl md:text-3xl text-purple-400 mb-4 animate-on-scroll">
                    {t('referral.subtitle')}
                  </p>

                  <p className="text-lg text-gray-300 mb-12 text-center animate-on-scroll max-w-3xl mx-auto">
                    {t(isAlt ? 'referral.copy.alternate' : 'referral.copy.default')}
                  </p>
                </div>

                <div className="hidden text-center mb-10">
                  <button
                    onClick={scrollToResidualIncomeCalculator}
                    className="inline-flex items-center px-6 py-3 b- bg-transparent border border-purple-500 text-purple-400 rounded-full hover:bg-purple-500/10 transition-all duration-300 animate-on-scroll will-change-transform"
                  >
                    {t('referralOpportunity.hero.button')}
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 mb-10">
                  {/* Left side - Referral structure visualization */}
                  <div hidden className="lg:w-1/2 animate-on-scroll">
                    <h3 className="text-xl font-semibold text-blak mb-4 text-center">
                      {t('referral.structure.title')}
                    </h3>

                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl pt-3 relative overflow-hidden h-full">
                      {/* Referral organogram */}
                      <div className="flex flex-col items-center">
                        {/* You - Level 0 */}
                        <div className="relative mb-2">
                          <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center z-20 relative">
                            <span className="text-white font-medium">{t('referral.you')}</span>
                          </div>
                        </div>

                        {/* Connection Line */}
                        <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-indigo-500"></div>

                        {/* Level 1 */}
                        <div className="mb-2">
                          <div className="flex items-center justify-center mb-2">
                            <span className="bg-purple-500/20 text-purple-300 text-s font-medium py-1 px-3 rounded-full">
                              {t('referral.level1Commission')}
                            </span>
                          </div>

                          <div className="flex space-x-12">
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 bg-indigo-500/30 rounded-full flex items-center justify-center mb-1">
                                <User size={20} className="text-indigo-300" />
                              </div>
                              <span className="text-s text-gray-400">{t('referral.directReferral')}</span>
                            </div>
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 bg-indigo-500/30 rounded-full flex items-center justify-center mb-1">
                                <User size={20} className="text-indigo-300" />
                              </div>
                              <span className="text-s text-gray-400">{t('referral.directReferral')}</span>
                            </div>
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 bg-indigo-500/30 rounded-full flex items-center justify-center mb-1">
                                <User size={20} className="text-indigo-300" />
                              </div>
                              <span className="text-s text-gray-400">{t('referral.directReferral')}</span>
                            </div>
                          </div>
                        </div>

                        {/* Updated: Connection Lines for Level 2 - more evenly distributed */}
                        <div className="w-2/3 h-px bg-indigo-500/30 mb-1"></div>
                        <div className="flex justify-between w-2/3 mb-2">
                          <div className="w-px h-4 bg-indigo-500/30"></div>
                          <div className="w-px h-4 bg-indigo-500/30"></div>
                          <div className="w-px h-4 bg-indigo-500/30"></div>
                          <div className="w-px h-4 bg-indigo-500/30"></div>
                          <div className="w-px h-4 bg-indigo-500/30"></div>
                        </div>

                        {/* Level 2-4 - Updated with evenly distributed icons */}
                        <div className="mb-2">
                          <div className="flex items-center justify-center mb-2">
                            <span className="bg-indigo-500/20 text-purple-300 text-s font-medium py-1 px-3 rounded-full">
                              {t('referral.levels2to4Commission')}
                            </span>
                          </div>

                          {/* Evenly distributed icons */}
                          <div className="flex justify-between w-3/8 mx-auto">
                            <div className="px-5 flex flex-col items-center">
                              <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                                <User size={20} className="text-indigo-300" />
                              </div>
                            </div>
                            <div className="px-5 flex flex-col items-center">
                              <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                                <User size={20} className="text-indigo-300" />
                              </div>
                            </div>
                            <div className="px-5 flex flex-col items-center">
                              <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                                <User size={20} className="text-indigo-300" />
                              </div>
                            </div>
                            <div className="px-5 flex flex-col items-center">
                              <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                                <User size={20} className="text-indigo-300" />
                              </div>
                            </div>
                            <div className="px-5 flex flex-col items-center">
                              <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                                <User size={20} className="text-indigo-300" />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Connection Line */}
                        <div className="w-1 h-3 bg-gradient-to-b from-indigo-500/30 to-blue-500/30"></div>

                        {/* Levels 5-6 */}
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-0">
                            <span className="bg-blue-500/20 text-purple-300 text-s font-medium py-1 px-3 rounded-full">
                              {t('referral.levels5to6Commission')}
                            </span>
                          </div>
                        </div>
                        {/* Connection Line */}
                        <div className="w-1 h-3 bg-gradient-to-b from-indigo-500/30 to-blue-500/30"></div>
                        {/* Levels 7 - 10*/}
                        <div className="text-center">
                          <div className="flex items-center justify-center m-1">
                            <span className="bg-blue-500/20 text-purple-300 text-s font-medium py-1 px-3 mx-3 rounded-full">
                              {t('referral.level7Commission')}
                            </span>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center m-1">
                            <span className="bg-blue-500/20 text-purple-300 text-s font-medium py-1 px-3 mx-3 rounded-full">
                              {t('referral.level8Commission')}
                            </span>

                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center m-1">
                            <span className="bg-blue-500/20 text-purple-300 text-s font-medium py-1 px-3 mx-3 rounded-full">
                              {t('referral.level9Commission')}
                            </span>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center m-1">
                            <span className="bg-blue-500/20 text-purple-300 text-s font-medium py-1 px-3 mx-3 rounded-full">
                              {t('referral.level10Commission')}
                            </span>
                          </div>
                        </div>

                        {/* Level Bottom  */}
                        <div className="text-center">
                          <div className="flex items-center justify-center m-2">
                            <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center">
                              <Users size={24} className="text-blue-300" />
                            </div>
                          </div>

                          <p className="text-gray-400 text-s max-w-xs mx-auto">
                            {r(t('referral.communityExpands'))}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Commission tiers table */}
                  <div className="lg:w-1/1 animate-on-scroll">
                    <h3 className="text-xl font-semibold text-white mb-4 text-center">
                      {t('referral.commission.title')}
                    </h3>
                    <p className="text-gray-300 text-center mb-6 max-w-3xl mx-auto animate-on-scroll">
                      {t(isAlt ? 'referral.commission.description.alternate' : 'referral.commission.description.default')}
                    </p>
                    <div className="flex justify-center">
                      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden h-full w-[80%]">
                        <div className="text-center p-4">
                          <p className="text-gray-300 mb-2">
                            {t(isAlt ? 'referral.earnCommissions.alternate' : 'referral.earnCommissions.default')}
                          </p>
                        </div>

                        <div className="flex justify-center text-center">
                          <table className="w-[90%] text-white ">
                            <thead>
                              <tr className="bg-white/10">
                                <th className="px-6 py-2 text-center">{t('referral.table.level')}</th>
                                <th className="px-6 py-2 text-center">{t('referral.table.commission')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tiers.map((tier) => (
                                <tr
                                  key={tier.level}
                                  className="border-t border-white/5 animate-on-scroll hover:bg-white/5 transition-colors"
                                  style={{ animationDelay: `${tier.delay}ms` }}
                                >
                                  <td className="px-6 py-2">
                                    {tier.level} {t('referral.levelSuffix')}
                                  </td>
                                  <td className="px-6 py-2 text-purple-400 font-medium">{tier.commission}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="px-6 py-0">
                        </div>
                        <div className="px-6 py-2 bg-white/5 my-4 mx-4 rounded-lg">
                          <div className="flex items-center mb-2">
                            <GitBranch className="text-purple-400 mr-2" size={18} />
                            <h4 className="text-white font-medium">{t('referral.example.title')}</h4>
                          </div>
                          <p className="text-gray-300 text-sm">
                            {t('referral.example.text')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ReferralOpportunityHero;
